import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, CircleNotch, WarningCircle, LockKey } from "@phosphor-icons/react";

export default function Onboard() {
  const [state, setState] = useState("checking"); // checking | form | error
  const [ctx, setCtx] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("e")) { setState("error"); return; }
    const token = params.get("join") || undefined;
    (async () => {
      try {
        const r = await api.get("/auth/join/context/info", token ? { params: { token } } : undefined);
        if (r.data?.activated) { navigate("/login?activated=1", { replace: true }); return; }
        if (r.data?.ok) { setCtx({ ...r.data, token }); setState("form"); } else setState("error");
      } catch { setState("error"); }
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 10) { setError("Password must be at least 10 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/join/complete", ctx?.token ? { password, token: ctx.token } : { password });
      navigate("/login?activated=1", { replace: true });
    } catch (err) {
      if (err?.response?.status === 409) { navigate("/login?activated=1", { replace: true }); return; }
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="onboard-page">
      <div className="max-w-md w-full border border-border bg-surface rounded-md p-8">
        <div className="h-10 w-10 bg-primary rounded flex items-center justify-center">
          <ShieldCheck size={22} weight="fill" className="text-white" />
        </div>

        {state === "checking" && (
          <p className="mt-5 t-support text-muted-foreground flex items-center gap-2">
            <CircleNotch size={16} className="animate-spin text-primary" weight="bold" /> Checking your invitation…
          </p>
        )}

        {state === "error" && (
          <>
            <div className="mt-5 h-10 w-10 flex items-center justify-center text-amber-600">
              <WarningCircle size={32} weight="duotone" />
            </div>
            <h1 className="mt-3 t-section-title" data-testid="onboard-error-title">Invitation unavailable</h1>
            <p className="mt-2 t-support text-muted-foreground">
              This invitation link cannot be used. It may be invalid, expired, revoked or already used.
              Please contact your District Administrator for a new invitation.
            </p>
          </>
        )}

        {state === "form" && (
          <form onSubmit={submit} data-testid="onboard-form">
            <p className="mt-5 text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Account activation</p>
            <h1 className="mt-2 font-display font-extrabold tracking-tighter text-2xl">
              Welcome, {ctx?.display_name} to the GOV platform
            </h1>
            <p className="mt-2 t-support text-muted-foreground">
              Your login id is <span className="font-mono font-semibold text-foreground" data-testid="onboard-email">{ctx?.email}</span>.
              Please set your password for secure access.
            </p>
            <label className="block mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Password</label>
            <input
              data-testid="onboard-password-input" type="password" required minLength={10} autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full border border-border bg-background px-4 py-3 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Minimum 10 characters. Long passphrases and password managers are welcome.
            </p>
            <label className="block mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Confirm password</label>
            <input
              data-testid="onboard-confirm-input" type="password" required autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 w-full border border-border bg-background px-4 py-3 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {error && <p data-testid="onboard-error" className="mt-3 text-sm text-red-700">{error}</p>}
            <button
              data-testid="onboard-submit-button" type="submit" disabled={submitting}
              className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {submitting ? <CircleNotch size={18} className="animate-spin" weight="bold" /> : <LockKey size={18} weight="bold" />}
              Activate my account
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
